import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, FileText, Mail, TicketPercent, AlertTriangle, Sparkles, ShoppingBag, IndianRupee } from "lucide-react";
import { fetchStats, fetchOrderStats, fetchSalesAnalytics } from "../api";

const PageHeader = ({ eyebrow, title, sub }) => (
  <header className="px-10 py-8 border-b border-[#2a2a2d] bg-[#0d0d0e] sticky top-0 z-10">
    <p className="text-[10px] tracking-[0.4em] uppercase text-[#8c8a7e]">{eyebrow}</p>
    <h1 className="font-serif-display text-4xl text-[#e9e6db] mt-2">{title}</h1>
    {sub && <p className="text-[13px] text-[#8c8a7e] mt-2">{sub}</p>}
  </header>
);

const Stat = ({ icon: Icon, label, value, accent, to }) => {
  const Inner = (
    <div className="bg-[#161617] border border-[#2a2a2d] p-7 hover:border-[#3a3a3d] transition-colors group">
      <div className="flex items-center justify-between">
        <Icon size={20} className={accent || "text-[#c3ba9e]/70"} />
        <span className="text-[10px] tracking-[0.32em] uppercase text-[#5e5d54] group-hover:text-[#8c8a7e] transition-colors">View →</span>
      </div>
      <p className="font-serif-display text-5xl text-[#e9e6db] mt-6 leading-none">
        {value === undefined || value === null ? "—" : value}
      </p>
      <p className="text-[11px] tracking-[0.28em] uppercase text-[#8c8a7e] mt-3">{label}</p>
    </div>
  );
  return to ? <Link to={to}>{Inner}</Link> : Inner;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [sales, setSales] = useState([]);

  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
    fetchOrderStats().then(setOrderStats).catch(() => {});
    fetchSalesAnalytics(30).then(setSales).catch(() => {});
  }, []);

  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <div data-testid="admin-dashboard">
      <PageHeader
        eyebrow="Overview · MMXXVI"
        title="Welcome back"
        sub="A quiet glance at your atelier today."
      />
      <div className="p-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Stat icon={IndianRupee} label="Revenue (lifetime)" value={orderStats ? fmtINR(orderStats.revenue) : "—"} accent="text-[#c3ba9e]" to="/admin/orders" />
          <Stat icon={ShoppingBag} label="Pending orders" value={orderStats?.pending_orders} accent="text-[#c79a3a]" to="/admin/orders" />
          <Stat icon={ShoppingBag} label="Total orders" value={orderStats?.total_orders} to="/admin/orders" />
          <Stat icon={Package} label="Products" value={stats?.products} to="/admin/products" />
          <Stat icon={FileText} label="Blog posts" value={stats?.blog_posts} to="/admin/blog" />
          <Stat icon={Mail} label="Newsletter subscribers" value={stats?.subscribers} to="/admin/subscribers" />
          <Stat icon={TicketPercent} label="Active coupons" value={stats?.active_coupons} to="/admin/coupons" />
          <Stat icon={AlertTriangle} label="Low stock (<15)" value={stats?.low_stock_products} accent="text-[#c79a3a]" to="/admin/products" />
          <Stat icon={Sparkles} label="New arrivals" value={stats?.new_arrivals} to="/admin/products" />
        </div>

        <section className="mt-12 bg-[#161617] border border-[#2a2a2d] p-8">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8c8a7e]">Last 30 days</p>
          <h3 className="font-serif-display text-2xl text-[#e9e6db] mt-1">Sales pulse</h3>
          <SalesChart data={sales} />
        </section>

        <section className="mt-12 bg-[#161617] border border-[#2a2a2d] p-8">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8c8a7e]">Quick actions</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Link to="/admin/orders" className="border border-[#2a2a2d] p-4 hover:bg-[#1f1f21] transition-colors text-[12px] text-[#c3ba9e] tracking-wider">→ Manage orders</Link>
            <Link to="/admin/products/new" className="border border-[#2a2a2d] p-4 hover:bg-[#1f1f21] transition-colors text-[12px] text-[#c3ba9e] tracking-wider">+ Add product</Link>
            <Link to="/admin/blog/new" className="border border-[#2a2a2d] p-4 hover:bg-[#1f1f21] transition-colors text-[12px] text-[#c3ba9e] tracking-wider">+ Write a story</Link>
            <Link to="/admin/sections" className="border border-[#2a2a2d] p-4 hover:bg-[#1f1f21] transition-colors text-[12px] text-[#c3ba9e] tracking-wider">Edit homepage copy</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export { PageHeader };

function SalesChart({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-[12px] text-[#8c8a7e] mt-4">No sales recorded yet.</p>;
  }
  const maxRev = Math.max(1, ...data.map((d) => d.revenue));
  const totalOrders = data.reduce((s, d) => s + d.orders, 0);
  const totalRev = data.reduce((s, d) => s + d.revenue, 0);
  const fmt = (n) => `₹${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
  return (
    <div className="mt-5">
      <div className="flex items-end gap-1.5 h-32" data-testid="sales-chart">
        {data.map((d) => {
          const h = Math.max(2, (d.revenue / maxRev) * 100);
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
              <div className="opacity-0 group-hover:opacity-100 text-[10px] text-[#c3ba9e] mb-1 transition-opacity">{fmt(d.revenue)}</div>
              <div
                className="w-full bg-[#c3ba9e]/30 hover:bg-[#c3ba9e] transition-colors"
                style={{ height: `${h}%` }}
                title={`${d.date}: ₹${d.revenue} · ${d.orders} orders`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-3 text-[10px] text-[#8c8a7e]">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
      <div className="flex gap-6 mt-4 text-[12px] text-[#9a9787]">
        <span><b className="text-[#c3ba9e]">{totalOrders}</b> orders</span>
        <span><b className="text-[#c3ba9e]">₹{totalRev.toLocaleString("en-IN")}</b> revenue</span>
      </div>
    </div>
  );
}
